import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import {
  createPriority,
  deletePriority,
  fetchPriorities,
  patchPriorities,
  selectTaskPriority,
  updatePriority
} from '@SonarCX/slices/priority'

import NoPermissionContainer from '@SonarCX/components/molecules/NoPermissionContainer'
import SettingsSection from '../../SettingsSection'
import Add from '../../Controls/Add'
import AddColor from '../../Controls/Add/AddColour'
import Dropdown from '@SonarCX/components/atoms/Dropdown'
import DraggableSetting from '../DraggableSetting'

import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'
import { useSettingsEdit } from '@SonarCX/providers/SettingsEditProvider'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import { DEFAULT_COLORS } from '../../Controls/Add/helpers'
import { ArrowDown2 } from 'iconsax-react'
import { doAlphabeticalSort, doDragDrop } from '../DraggableSetting/helpers'

import useStyles from './../StatusSettings/styles'

const MAP_BASE = {
  None: null,
  Critical: 'critical',
  Low: 'low'
}

const PrioritySettings = () => {
  const dispatch = useAppDispatch()

  const {
    priorities,
    loaded: taskPriorityLoaded,
    loading: taskPriorityLoading,
    error
  } = useAppSelector(selectTaskPriority)

  const { setEditObject } = useSettingsEdit()
  const hasPermission = useAccess()

  const [saving, setSaving] = React.useState(false)
  const [color, setColor] = React.useState(null)
  const [base, setBase] = React.useState('None')

  const classes = useStyles()
  const handleAddPriority = React.useCallback(
    async name => {
      try {
        setSaving(true)
        const randomColor =
          DEFAULT_COLORS[
            Math.floor(Math.random() * DEFAULT_COLORS.length)
          ].toLowerCase()

        await dispatch(
          createPriority({
            name: name,
            flow_order: null,
            colour: color ? color.toLowerCase() : randomColor,
            base_priority: MAP_BASE[base]
          })
        )
        dispatch(AddSuccessAlert('Changes saved'))
        setColor(null)
        setBase('None')
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      } finally {
        setSaving(false)
      }
    },
    [base, color, dispatch]
  )

  const handleEditPriority = React.useCallback(
    async save => {
      //transform object
      const toSave = { ...save }
      toSave.colour = toSave.colour?.toLowerCase() ?? null
      toSave.colour =
        toSave.colour && toSave.colour[0] === '#'
          ? toSave.colour.substring(1)
          : toSave.colour

      await dispatch(updatePriority(toSave))
      dispatch(AddSuccessAlert('Changes saved'))
      // resetting object after save
      setEditObject(null)
    },
    [setEditObject, dispatch]
  )

  const handleDeletePriority = React.useCallback(
    obj => {
      try {
        dispatch(deletePriority(obj.id))
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        console.error(err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [dispatch]
  )

  const handleDragDrop = (
    sourceID: string,
    targetID: string,
    after?: boolean
  ) => {
    const toUpdate = doDragDrop(priorities, sourceID, targetID, after)
    dispatch(patchPriorities(toUpdate))
  }

  const handleSort = React.useCallback(
    (ascending: boolean) => {
      dispatch(patchPriorities(doAlphabeticalSort(ascending, priorities)))
    },
    [priorities, dispatch]
  )

  React.useEffect(() => {
    if (!taskPriorityLoaded && !taskPriorityLoading) {
      dispatch(fetchPriorities())
    }
  }, [dispatch, taskPriorityLoaded, taskPriorityLoading])

  if (!hasPermission(PERMS.TasksAdmin)) {
    return null
  }

  return (
    <NoPermissionContainer error={error} skipAgentPage>
      <SettingsSection
        title='Priorities'
        loaded={taskPriorityLoaded}
        sort={handleSort}
      >
        {priorities.map((priority, i) => {
          return (
            <DraggableSetting
              key={priority.id}
              obj={priority}
              objType='priority'
              superType='task'
              handleDelete={handleDeletePriority}
              handleEdit={handleEditPriority}
              isLast={i === priorities.length - 1}
              index={i}
              onDragDrop={handleDragDrop}
            />
          )
        })}

        <Add
          superType='task'
          type='priority'
          handleAdd={handleAddPriority}
          saving={saving}
        >
          <AddColor saveColor={setColor} prevColor={color} />

          <Dropdown
            options={['None', 'Critical', 'Low']}
            selected={base ?? 'None'}
            onSelect={val => setBase(val)}
            hideAdd
            hideClose
            stringOnly
          >
            <div className={classes.root}>
              <span style={{ paddingRight: '1rem' }}>{base ?? 'Open'}</span>

              <ArrowDown2 className={classes.icon} />
            </div>
          </Dropdown>
        </Add>
      </SettingsSection>
    </NoPermissionContainer>
  )
}

export default PrioritySettings
